import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { useStoreContext } from "@components/store/provider";
import { useConfig } from "@stadium/hooks/lib/use-config";

import {
  getFormattedBrandValues,
  getFormattedOptionValues,
  getFormattedProductionDays,
  getItemFeatures,
  getPriceSliderRanges,
  getFormattedPrintTypeValues,
  getPriceHistogram,
  getDiscretePriceRangeOptions,
  getFirstSelectableCollectionId,
  shouldPreselectCollectionFilter,
} from "@helpers/products/search";
import { toPreselectFilterValues } from "@stadium/shared/src/components/filter/helpers";
import { getCurrencySymbol } from "@helpers/currency";
import { isCustomCurrency } from "@stadium/helpers/currency";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import {
  getIsBundleShop,
  getIsLockerShop,
  getIsPricelessShop,
  getStoreCurrencyName,
  getStoreCurrencyPrefix,
  getStoreCurrencySuffix,
  getIsHideBulkPricing,
} from "@selectors/store";
import { useWorkspaceContext } from "@contexts/workspace-context";
import { selectRestrictionTaxons } from "@stadium/redux-utils/selectors/configSelector";
import {
  NEW_CUSTOM_FILTERS,
  PRICE_FILTER_KEY,
  NEW_WORKSPACE_SHARING_DESIGNED_FILTERS,
  MENU_CATEGORY_FILTER_KEY,
  OPTION_VALUES_KEY,
  FILTERED_COLOR_VALUES_KEY,
  COLOR_VALUES_KEY,
  FILTERED_SIZE_VALUES_KEY,
  SIZE_VALUES_KEY,
  ALL_PRODUCTS_FILTER,
  PRODUCTION_DAYS_FILTER_KEY,
  DECORATION_METHOD_KEY,
  DESIGNED_TYPE_FILTER_KEY,
  ITEM_TYPE_FILTER_KEY,
  ITEM_FEATURES_FILTER_KEY,
  BRAND_FILTER_KEY,
  COLLECTION_FILTER_KEY,
  COLOR_FILTER_KEY,
  SIZE_FILTER_KEY,
  DIETARY_RESTRICTIONS_FILTER_KEY,
  USD_PRICE_RANGE_KEY,
  USD_PRICE_STATS_KEY,
} from "@constants/products/listing";
import { MY_DESIGNED_SWAG_LOCKER } from "@constants/menu";
import { US_CURRENCY_CODE } from "@constants/store";
import { getMenu } from "@selectors/menu";
import {
  getNestedOrderedMenuItems,
  createMenuCodeMapping,
  getTopLevelMenuItem,
} from "@helpers/menu";
import { ALL_SUBCATEGORY_CODE, ALL_PRODUCTS, getMenuItemRoute } from "@constants/routes";
import { FilterType } from "@stadium/shared/src/components/filter/types";

const DEFAULT_ARR = [];
const DEFAULT_OBJ = {};

const DEFAULT_FILTER_TYPES = {
  [DESIGNED_TYPE_FILTER_KEY]: FilterType.SINGLE_SELECT,
  [MENU_CATEGORY_FILTER_KEY]: FilterType.LINK,
  [PRICE_FILTER_KEY]: FilterType.RANGE,
  [DIETARY_RESTRICTIONS_FILTER_KEY]: FilterType.MULTI_SELECT,
  [BRAND_FILTER_KEY]: FilterType.MULTI_SELECT,
  [COLLECTION_FILTER_KEY]: FilterType.MULTI_SELECT,
  [COLOR_FILTER_KEY]: FilterType.MULTI_SELECT,
  [SIZE_FILTER_KEY]: FilterType.MULTI_SELECT,
  [DECORATION_METHOD_KEY]: FilterType.MULTI_SELECT,
  [PRODUCTION_DAYS_FILTER_KEY]: FilterType.MULTI_SELECT,
  [ITEM_FEATURES_FILTER_KEY]: FilterType.MULTI_SELECT,
  [ITEM_TYPE_FILTER_KEY]: FilterType.SINGLE_SELECT,
};

/**
 * Maps current category's children to filter options.
 * - Items with NO children: flat radio option (selectable)
 * - Items WITH children: automatically become group headers (dropdown, not selectable), children are radio
 * - Top-level items (depth 0) without children get `hideRadioIcon: true` so the radio
 *   input remains functional but the icon is visually hidden in the Filter component.
 */
const mapCurrentCategoryChildrenToFilterOptions = (
  menuItems,
  currentSubCategory,
  getHref,
  showViewAll = true,
  depth = 0,
) => {
  if (!menuItems?.length) return [];

  return menuItems
    .filter((item) => item && !item.isDisabled)
    .map((item) => {
      const hasChildren = item.children?.length > 0;
      const isTopLevel = depth === 0;
      const itemCode = item.code === ALL_PRODUCTS ? "" : item.code;

      // Base option representing this menu item as a selectable leaf
      const baseOption = {
        id: String(item.id),
        label: item.name,
        value: itemCode,
        hideRadioIcon: !hasChildren && isTopLevel,
        isSelected: itemCode === currentSubCategory,
        href: getHref(itemCode),
      };

      if (!hasChildren) {
        // Simple leaf: just return a selectable radio option
        return baseOption;
      }

      // For items with children, we create a group header whose children are radios.
      // On the search/root page, this lets us put "All <Category>" as the first child
      // under each top-level category (e.g. Apparel) so users can browse all items
      // in that category as well as its subcategories.
      const childOptions = mapCurrentCategoryChildrenToFilterOptions(
        item.children,
        currentSubCategory,
        getHref,
        showViewAll,
        depth + 1,
      );

      const childrenWithAllOption =
        isTopLevel || childOptions.length > 0
          ? [
              {
                // Synthetic "View All" child option for this parent
                id: `${item.id}-all`,
                label: `All ${item.name}`,
                value: item.code,
                isSelected: item.code === currentSubCategory,
                href: getHref(item.code),
              },
              ...childOptions,
            ]
          : childOptions;

      return {
        id: String(item.id),
        label: item.name,
        value: item.code,
        children: childrenWithAllOption,
        childType: FilterType.LINK,
        childId: MENU_CATEGORY_FILTER_KEY,
        href: getHref(item.code), // Group labels are also clickable links
        showSearch: false,
        showViewAll,
      };
    });
};
/** Build filter config sections from aggregations and context. Each returns a config item or null. */
function buildWorkspaceSharingSection(opts) {
  const {
    isPersonalWorkspace,
    isBundleShop,
    isLockerShop,
    isMyDesignedSwag,
    type,
    hasCollectionFilter,
  } = opts;
  const shouldAdd = !isPersonalWorkspace && (isBundleShop || (isLockerShop && isMyDesignedSwag));

  if (!shouldAdd) return null;

  return {
    id: DESIGNED_TYPE_FILTER_KEY,
    label: "Workspace Sharing",
    type: type || DEFAULT_FILTER_TYPES[DESIGNED_TYPE_FILTER_KEY],
    displayType: "list",
    defaultOpen: !hasCollectionFilter,
    options: NEW_WORKSPACE_SHARING_DESIGNED_FILTERS.map((f) => ({
      ...f,
      value: f.id,
    })),
  };
}

function buildCategorySection({
  currentCategoryItem,
  nestedMenuItems,
  opts = DEFAULT_OBJ,
} = DEFAULT_OBJ) {
  const { storeBasePath, currentSubCategory } = opts;

  const getHrefForCategory = (value) => {
    const routeCategory = value || ALL_PRODUCTS;

    const route = getMenuItemRoute({
      storeBasePath,
      category: routeCategory,
    });

    return route.pathname;
  };

  const showViewAll = false;
  const categoryChildren = currentCategoryItem?.children ?? nestedMenuItems?.children ?? [];
  const categoryOptions = mapCurrentCategoryChildrenToFilterOptions(
    categoryChildren,
    currentSubCategory,
    getHrefForCategory,
    showViewAll,
  );
  if (categoryOptions.length === 0) return null;

  // When we're on a specific category (e.g. Apparel), prepend a synthetic
  // "View All" option that maps to "no menu_category filter".
  if (currentCategoryItem) {
    const isAllSelected = !currentSubCategory || currentSubCategory === ALL_SUBCATEGORY_CODE;
    const allOption = {
      id: `${MENU_CATEGORY_FILTER_KEY}-all`,
      label: `All ${currentCategoryItem.name}`,
      value: "",
      isSelected: isAllSelected,
      hideRadioIcon: true,
      href: getHrefForCategory(currentCategoryItem.code),
    };

    return {
      id: MENU_CATEGORY_FILTER_KEY,
      label: "Product Types",
      type: opts.type || DEFAULT_FILTER_TYPES[MENU_CATEGORY_FILTER_KEY],
      displayType: "list",
      options: [allOption, ...categoryOptions],
      defaultOpen: true,
      showViewAll,
    };
  }

  // On root/search, just show real top-level categories (no synthetic All Products).
  return {
    id: MENU_CATEGORY_FILTER_KEY,
    label: "Product Types",
    type: opts.type || DEFAULT_FILTER_TYPES[MENU_CATEGORY_FILTER_KEY],
    displayType: "list",
    showViewAll, // Category menus often determine their own view states
    options: categoryOptions,
  };
}

function buildPriceRangeSection({ aggregations, opts } = DEFAULT_OBJ) {
  const {
    type,
    isPricelessShop,
    convertPriceInDisplayStoreCurrency,
    storeDisplayCurrencyCode,
    storeCurrencyName,
    displayPrefix,
    displaySuffix,
  } = opts;

  if (isPricelessShop) return null;

  const resolvedType = type || DEFAULT_FILTER_TYPES[PRICE_FILTER_KEY];
  const isDiscrete =
    resolvedType === FilterType.SINGLE_SELECT || resolvedType === FilterType.MULTI_SELECT;

  if (isDiscrete) {
    const buckets = aggregations?.prices_range?.[USD_PRICE_RANGE_KEY]?.buckets;
    const priceStats = aggregations?.price_range?.[USD_PRICE_STATS_KEY];
    const options = getDiscretePriceRangeOptions(buckets, priceStats, {
      displayPrefix,
      displaySuffix,
      convertPrice: (val) => convertPriceInDisplayStoreCurrency(val, US_CURRENCY_CODE),
    });

    if (options.length === 0) return null;

    return {
      id: PRICE_FILTER_KEY,
      label: "Price Range",
      type: resolvedType,
      displayType: "list",
      options,
    };
  }

  const priceStats = aggregations?.price_range?.usd_price_stats;
  if (!priceStats) return null;

  const priceRangeData = getPriceSliderRanges(priceStats, convertPriceInDisplayStoreCurrency);
  const histogramData = getPriceHistogram(aggregations, convertPriceInDisplayStoreCurrency);

  return {
    id: PRICE_FILTER_KEY,
    label: "Price Range",
    type: resolvedType,
    displayType: "row",
    options: {
      ...priceRangeData,
      histogramData,
      currencyCode: storeDisplayCurrencyCode,
      currencyName: storeCurrencyName,
      currencySymbol: displayPrefix || storeDisplayCurrencyCode,
      prefix: displayPrefix,
      suffix: displaySuffix,
      selectedCurrency: storeDisplayCurrencyCode,
      step: 1,
    },
  };
}

function buildDietarySection({ aggregations, restrictionTaxons, type } = DEFAULT_OBJ) {
  const buckets = (aggregations.external_dietary_restrictions || aggregations.dietary_restrictions)
    ?.buckets;
  if (!buckets?.length) return null;
  const options = buckets
    .map((data) => {
      const matchingTaxon = restrictionTaxons?.find(
        (taxon) => Number(taxon.id) === Number(data.key),
      );
      if (!matchingTaxon) return null;
      return {
        id: data.key,
        label: matchingTaxon.name,
        value: data.key,
      };
    })
    .filter(Boolean);

  if (options.length === 0) return null;

  return {
    id: DIETARY_RESTRICTIONS_FILTER_KEY,
    label: "Dietary Restrictions",
    type: type || DEFAULT_FILTER_TYPES[DIETARY_RESTRICTIONS_FILTER_KEY],
    displayType: "list",
    options,
  };
}

function buildBrandsSection({ aggregations, type } = DEFAULT_OBJ) {
  const brandsData = getFormattedBrandValues(aggregations.brands || []);
  if (!brandsData?.length) return null;
  return {
    id: BRAND_FILTER_KEY,
    label: "Brands",
    type: type || DEFAULT_FILTER_TYPES[BRAND_FILTER_KEY],
    displayType: "list",
    options: brandsData.map((item) => ({
      ...item,
      label: item.name || item.label,
      value: item.id,
    })),
  };
}

function buildCollectionsSection({
  collectionOptions,
  isMyDesignedSwag,
  type,
  filterPreselect,
} = DEFAULT_OBJ) {
  if (!isMyDesignedSwag || !collectionOptions?.length) return null;
  const { preselectThreshold, baseDesignedStoreIds } = filterPreselect || DEFAULT_OBJ;
  const firstSelectableId = getFirstSelectableCollectionId(collectionOptions, baseDesignedStoreIds);
  const preselectdValues = toPreselectFilterValues(
    firstSelectableId == null ? [] : [firstSelectableId],
  );

  return {
    id: COLLECTION_FILTER_KEY,
    label: "Collections",
    type: type || DEFAULT_FILTER_TYPES[COLLECTION_FILTER_KEY],
    displayType: "list",
    options: collectionOptions,
    defaultOpen: true,
    ...(preselectThreshold == null ? {} : { preselectThreshold }),
    ...(shouldPreselectCollectionFilter(
      collectionOptions,
      baseDesignedStoreIds,
      preselectThreshold,
    ) && preselectdValues.length
      ? { preselectdValues }
      : {}),
  };
}

function buildOptionSection({
  aggregations,
  key,
  label,
  optionPath,
  type,
  displayType = "grid",
  columns = 4,
  prefixInfo,
} = DEFAULT_OBJ) {
  const values = optionPath.reduce((acc, k) => acc?.[k], aggregations);
  const optionsData = getFormattedOptionValues(values);
  if (optionsData?.length <= 1) return null;

  return {
    id: key,
    label,
    type: type || DEFAULT_FILTER_TYPES[key],
    displayType,
    columns: displayType === "grid" ? columns : undefined,
    options: optionsData.map((item) => {
      const { key: prefixKey, getValue } = prefixInfo || DEFAULT_OBJ;
      const prefix = getValue?.(item?.[prefixKey]);
      return {
        ...item,
        label: item.name || item.label,
        value: item.id,
        ...(prefix ? { prefix } : {}),
      };
    }),
  };
}

function buildPrintTypesSection({ aggregations, isLockerShop, type } = DEFAULT_OBJ) {
  if (!isLockerShop) return null;
  const printValues = getFormattedPrintTypeValues(aggregations?.print_types);
  if (!printValues?.length) return null;
  return {
    id: DECORATION_METHOD_KEY,
    label: "Decoration Method",
    type: type || DEFAULT_FILTER_TYPES[DECORATION_METHOD_KEY],
    displayType: "list",
    options: printValues.map((item) => ({
      ...item,
      label: item.name || item.label,
      value: item.id,
    })),
  };
}

function buildProductionDaysSection({ aggregations, type } = DEFAULT_OBJ) {
  const productionDaysData = getFormattedProductionDays(aggregations.production_days || []);
  if (!productionDaysData?.length) return null;
  return {
    id: PRODUCTION_DAYS_FILTER_KEY,
    label: "Production Days",
    type: type || DEFAULT_FILTER_TYPES[PRODUCTION_DAYS_FILTER_KEY],
    displayType: "list",
    options: productionDaysData.map((item) => ({
      ...item,
      label: item.name || item.label,
      value: item.id,
    })),
  };
}

function buildItemFeaturesSection({ aggregations, type } = DEFAULT_OBJ) {
  const ecoFriendlyProductCount = Number(aggregations.eco_friendly?.doc_count || 0);
  const sustainableProductCount = Number(aggregations.sustainable?.doc_count || 0);
  const shippedFromStadiumProductsCount = Number(aggregations.shipped_from_stadium?.doc_count || 0);
  const featuresData = getItemFeatures({
    ecoFriendlyProductCount,
    sustainableProductCount,
    shippedFromStadiumProductsCount,
  });
  if (!featuresData?.length) return null;
  return {
    id: ITEM_FEATURES_FILTER_KEY,
    label: "Attributes",
    type: type || DEFAULT_FILTER_TYPES[ITEM_FEATURES_FILTER_KEY],
    displayType: "list",
    options: featuresData.map((item) => ({
      ...item,
      label: item.name || item.label,
      value: item.id,
    })),
  };
}

function buildItemTypeSection(opts) {
  const { isBundleShop, bulkProductsCount, onDemandProductsCount, type } = opts;
  const itemTypeFilterEnabled = bulkProductsCount || onDemandProductsCount;
  if (!isBundleShop && !itemTypeFilterEnabled) return null;
  const itemTypeFilterData = NEW_CUSTOM_FILTERS.itemType(opts);
  // Remove "All Products" — it's the default state, not a meaningful filter option
  const filteredItems = itemTypeFilterData.items.filter(
    (item) => item.id !== ALL_PRODUCTS_FILTER.id,
  );
  if (filteredItems.length <= 1) return null;
  return {
    id: ITEM_TYPE_FILTER_KEY,
    label: itemTypeFilterData.label,
    type: type || DEFAULT_FILTER_TYPES[ITEM_TYPE_FILTER_KEY],
    displayType: "list",
    options: filteredItems.map((item) => ({
      ...item,
      label: item.label || item.name,
      value: item.id,
      childId: item.childId || (item.children ? ITEM_TYPE_FILTER_KEY : undefined),
    })),
  };
}
const DEFAULT_FILTER_KEYS_TO_HIDE = [MENU_CATEGORY_FILTER_KEY];

const useFilterConfig = ({
  aggregations,
  hideFilterKeys = DEFAULT_FILTER_KEYS_TO_HIDE,
  defaultFilterTypes = DEFAULT_OBJ,
  collectionOptions = DEFAULT_ARR,
  enableCollectionFilter = false,
  filterPreselect = null,
}) => {
  const { convertPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();
  const { storeDisplayCurrencyCode, category, subCategory, storeBasePath } = useStoreContext();
  const storeCurrencyName = useSelector(getStoreCurrencyName);
  const storeCurrencySuffix = useSelector(getStoreCurrencySuffix);
  const storeCurrencyPrefix = useSelector(getStoreCurrencyPrefix);
  const restrictionTaxons = useConfig(selectRestrictionTaxons);
  const isLockerShop = useSelector(getIsLockerShop);
  const isBundleShop = useSelector(getIsBundleShop);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const { isPersonalWorkspace } = useWorkspaceContext();
  const isHideBulkPricing = useSelector(getIsHideBulkPricing);
  const menu = useSelector(getMenu);
  const { allChildren: menuItems = DEFAULT_ARR } = menu || DEFAULT_OBJ;
  const nestedMenuItems = useMemo(() => getNestedOrderedMenuItems({ menuItems }), [menuItems]);

  const filterTypes = useMemo(
    () => ({
      ...DEFAULT_FILTER_TYPES,
      ...defaultFilterTypes,
    }),
    [defaultFilterTypes],
  );

  const isMyDesignedSwag = category === MY_DESIGNED_SWAG_LOCKER;

  // Current category whose children we show as filters (always use the top-level category code)
  // We prioritize the leaf subcategory if it's available and not "all"
  const currentCategoryCode =
    subCategory && subCategory !== ALL_SUBCATEGORY_CODE ? subCategory : category;

  const menuCodeMapping = useMemo(
    () => (nestedMenuItems ? createMenuCodeMapping(nestedMenuItems) : {}),
    [nestedMenuItems],
  );

  const topLevelCategoryItem = useMemo(() => {
    return currentCategoryCode ? getTopLevelMenuItem(currentCategoryCode, menuCodeMapping) : null;
  }, [currentCategoryCode, menuCodeMapping]);

  const lastConfigRef = useRef({ category: null, config: null });

  const filterConfig = useMemo(() => {
    const collectionsSection = enableCollectionFilter
      ? buildCollectionsSection({
          collectionOptions,
          isMyDesignedSwag,
          type: filterTypes[COLLECTION_FILTER_KEY],
          filterPreselect: filterPreselect?.id === COLLECTION_FILTER_KEY ? filterPreselect : null,
        })
      : null;

    const hasAggregations = aggregations && Object.keys(aggregations).length > 0;
    if (!hasAggregations) {
      if (collectionsSection) {
        const earlyConfig = [collectionsSection];
        lastConfigRef.current = { category, config: earlyConfig };
        return earlyConfig;
      }
      return lastConfigRef.current.category === category ? lastConfigRef.current.config : undefined;
    }

    const isCustom = isCustomCurrency(storeDisplayCurrencyCode);
    const displayPrefix = isCustom
      ? storeCurrencyPrefix
      : getCurrencySymbol(storeDisplayCurrencyCode);
    const displaySuffix = isCustom ? storeCurrencySuffix : "";
    const bulkProductsCount = Number(aggregations.bulk_available?.doc_count || 0);
    const onDemandProductsCount = Number(aggregations.no_min_quantity?.doc_count || 0);
    const coreOnDemandProductsCount = isLockerShop
      ? Number(aggregations.core_on_demand?.doc_count || 0)
      : 0;
    const volumeDiscountProductsCount = isHideBulkPricing
      ? 0
      : Number(aggregations.has_tier_pricing?.doc_count || 0);

    const priceOpts = {
      convertPriceInDisplayStoreCurrency,
      storeDisplayCurrencyCode,
      storeCurrencyName,
      displayPrefix,
      displaySuffix,
      isPricelessShop,
      type: filterTypes[PRICE_FILTER_KEY],
    };
    const itemTypeOpts = {
      isBundleShop,
      bulkProductsCount,
      onDemandProductsCount,
      volumeDiscountProductsCount,
      coreOnDemandProductsCount,
      type: filterTypes[ITEM_TYPE_FILTER_KEY],
    };
    const workspaceOpts = {
      isPersonalWorkspace,
      isBundleShop,
      isLockerShop,
      isMyDesignedSwag,
      type: filterTypes[DESIGNED_TYPE_FILTER_KEY],
      hasCollectionFilter: !!collectionsSection,
    };

    const categoryOpts = {
      storeBasePath,
      currentSubCategory: subCategory,
      type: filterTypes[MENU_CATEGORY_FILTER_KEY],
    };

    const sections = [
      collectionsSection,
      buildWorkspaceSharingSection(workspaceOpts),
      buildCategorySection({
        currentCategoryItem: topLevelCategoryItem,
        nestedMenuItems,
        opts: categoryOpts,
      }),
      buildDietarySection({
        aggregations,
        restrictionTaxons,
        type: filterTypes[DIETARY_RESTRICTIONS_FILTER_KEY],
      }),
      buildItemTypeSection(itemTypeOpts),
      buildPriceRangeSection({ aggregations, opts: priceOpts }),
      buildItemFeaturesSection({
        aggregations,
        type: filterTypes[ITEM_FEATURES_FILTER_KEY],
      }),
      buildBrandsSection({
        aggregations,
        type: filterTypes[BRAND_FILTER_KEY],
      }),
      buildOptionSection({
        aggregations,
        key: COLOR_FILTER_KEY,
        label: "Colors",
        optionPath: [OPTION_VALUES_KEY, FILTERED_COLOR_VALUES_KEY, COLOR_VALUES_KEY],
        type: filterTypes[COLOR_FILTER_KEY],
        displayType: "grid",
        columns: 2,
        prefixInfo: {
          key: "hex_value",
          getValue: (hex) => (
            <span
              style={{
                backgroundColor: hex,
                width: "14px",
                height: "14px",
                marginRight: "8px",
                border: "solid 1px #eff0f2",
                display: "inline-block",
                borderRadius: "50%",
              }}
            ></span>
          ),
        },
      }),
      buildOptionSection({
        aggregations,
        key: SIZE_FILTER_KEY,
        label: "Sizes",
        optionPath: [OPTION_VALUES_KEY, FILTERED_SIZE_VALUES_KEY, SIZE_VALUES_KEY],
        type: filterTypes[SIZE_FILTER_KEY],
        displayType: "list",
      }),
      buildPrintTypesSection({
        aggregations,
        isLockerShop,
        type: filterTypes[DECORATION_METHOD_KEY],
      }),
      buildProductionDaysSection({
        aggregations,
        type: filterTypes[PRODUCTION_DAYS_FILTER_KEY],
      }),
    ];

    let config = sections.filter(Boolean);
    if (hideFilterKeys.length > 0) {
      config = config.filter((c) => !hideFilterKeys.includes(c.id));
    }

    lastConfigRef.current = { category, config };
    return config;
  }, [
    aggregations,
    nestedMenuItems,
    topLevelCategoryItem,
    storeDisplayCurrencyCode,
    isLockerShop,
    isBundleShop,
    isPricelessShop,
    storeCurrencyName,
    storeCurrencySuffix,
    storeCurrencyPrefix,
    convertPriceInDisplayStoreCurrency,
    restrictionTaxons,
    hideFilterKeys,
    isPersonalWorkspace,
    category,
    isMyDesignedSwag,
    isHideBulkPricing,
    filterTypes,
    subCategory,
    storeBasePath,
    collectionOptions,
    enableCollectionFilter,
    filterPreselect,
  ]);
  return filterConfig;
};

export default useFilterConfig;
